import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  allocatePartsRequests,
  createPurchaseOrdersFromRequests,
  getPartsRequestQueue,
  updatePartsRequest,
  type PartsRequestQueueGroup,
} from '@/lib/api/parts-requests.js';
import { listSuppliers } from '@/lib/api/suppliers.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency, prettyLabel } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function ageLabel(hours: number): string {
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.max(1, Math.round(hours / 24));
  return `${days}d ago`;
}

export function PartsRequestsPage() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<'grouped' | 'flat'>('grouped');
  const [status, setStatus] = useState('open');
  const [supplierId, setSupplierId] = useState('');
  const [minAgeDays, setMinAgeDays] = useState('');
  const [hasStock, setHasStock] = useState('');
  const [promisedFrom, setPromisedFrom] = useState('');
  const [promisedTo, setPromisedTo] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [qtyDraft, setQtyDraft] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const suppliersQuery = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: () => listSuppliers({ active: true }),
  });

  const queueQuery = useQuery({
    queryKey: ['parts-request-queue', view, status, supplierId, minAgeDays, hasStock, promisedFrom, promisedTo],
    queryFn: () =>
      getPartsRequestQueue({
        view,
        status,
        supplierId: supplierId || undefined,
        minAgeDays: minAgeDays ? Number(minAgeDays) : undefined,
        hasStock: hasStock || undefined,
        promisedFrom: promisedFrom ? new Date(promisedFrom).toISOString() : undefined,
        promisedTo: promisedTo ? new Date(promisedTo).toISOString() : undefined,
      }),
  });

  const groups = queueQuery.data?.groups ?? [];
  const flat = queueQuery.data?.requests ?? [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  function toggleGroup(group: PartsRequestQueueGroup) {
    const ids = group.requests.filter((row) => row.status === 'open').map((row) => row.id);
    const allOn = ids.every((id) => selectedSet.has(id));
    setSelected((current) => (allOn ? current.filter((id) => !ids.includes(id)) : [...new Set([...current, ...ids])]));
  }

  const selectedGroups = groups
    .map((group) => ({
      ...group,
      requests: group.requests.filter((row) => selectedSet.has(row.id)),
    }))
    .filter((group) => group.requests.length > 0);

  const supplierCount = new Set(selectedGroups.map((group) => group.preferredSupplierId ?? 'unassigned')).size;

  const poMutation = useMutation({
    mutationFn: () =>
      createPurchaseOrdersFromRequests({
        requestIds: selected,
        lineQuantities: selectedGroups
          .filter((group) => group.inventoryItemId)
          .map((group) => ({
            inventoryItemId: group.inventoryItemId!,
            qty: Number(qtyDraft[group.key] || group.need || 1),
          })),
      }),
    onSuccess: (data) => {
      const numbers = data.purchaseOrders.map((po) => po.poNumber).join(', ');
      toastSuccess(numbers ? `Draft purchase orders ${numbers}` : 'Purchase orders created');
      if (data.skipped.length) toastError(`${data.skipped.length} request(s) were skipped`);
      setConfirmOpen(false);
      setSelected([]);
      void queryClient.invalidateQueries({ queryKey: ['parts-request-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['parts-requests-open-count'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const allocateMutation = useMutation({
    mutationFn: (ids: string[]) => allocatePartsRequests(ids),
    onSuccess: (data) => {
      const tickets = data.fulfilled.map((row) => row.ticketNumber).join(', ');
      toastSuccess(
        data.leftOrdered
          ? `Allocated to ${tickets}. ${data.leftOrdered} request(s) still waiting on stock.`
          : `Allocated to ${tickets}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['parts-request-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['parts-requests-open-count'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const cancelMutation = useMutation({
    mutationFn: () => updatePartsRequest(cancelId!, { status: 'cancelled', reason: cancelReason.trim() }),
    onSuccess: () => {
      toastSuccess('Request cancelled');
      setCancelId(null);
      setCancelReason('');
      void queryClient.invalidateQueries({ queryKey: ['parts-request-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['parts-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['parts-requests-open-count'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-950">Parts requests</h1>
          <p className="text-sm text-slate-500">
            {queueQuery.data?.openCount ?? 0} open across the store. The same part on several tickets is one order line.
          </p>
        </div>
        <div className="flex rounded-xl bg-slate-100 p-1 text-sm font-semibold">
          {(['grouped', 'flat'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              className={mode === view ? 'rounded-lg bg-white px-3 py-1.5 text-slate-950 shadow-sm' : 'px-3 py-1.5 text-slate-600'}
              onClick={() => setView(mode)}
            >
              {mode === 'grouped' ? 'By part' : 'By ticket'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border bg-white p-4 md:grid-cols-3 xl:grid-cols-6">
        <label className="text-sm">
          <span className="font-medium text-slate-700">Status</span>
          <select className="mt-1 w-full rounded-xl border px-3 py-2" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="open">Open</option>
            <option value="ordered">Ordered</option>
            <option value="in_transit">In transit</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
            <option value="all">All</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="font-medium text-slate-700">Supplier</span>
          <select className="mt-1 w-full rounded-xl border px-3 py-2" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Any</option>
            {(suppliersQuery.data ?? []).map((supplier) => (
              <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="font-medium text-slate-700">Older than (days)</span>
          <input className="mt-1 w-full rounded-xl border px-3 py-2" type="number" min="0" value={minAgeDays} onChange={(e) => setMinAgeDays(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className="font-medium text-slate-700">Stock</span>
          <select className="mt-1 w-full rounded-xl border px-3 py-2" value={hasStock} onChange={(e) => setHasStock(e.target.value)}>
            <option value="">Any</option>
            <option value="true">In stock</option>
            <option value="false">None on hand</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="font-medium text-slate-700">Promised from</span>
          <input className="mt-1 w-full rounded-xl border px-3 py-2" type="date" value={promisedFrom} onChange={(e) => setPromisedFrom(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className="font-medium text-slate-700">Promised to</span>
          <input className="mt-1 w-full rounded-xl border px-3 py-2" type="date" value={promisedTo} onChange={(e) => setPromisedTo(e.target.value)} />
        </label>
      </div>

      {selected.length > 0 ? (
        <div className="flex items-center justify-between rounded-2xl bg-primary/5 px-4 py-3">
          <p className="text-sm font-medium text-slate-800">{selected.length} selected</p>
          <button type="button" className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white" onClick={() => setConfirmOpen(true)}>
            Create PO
          </button>
        </div>
      ) : null}

      {queueQuery.isLoading ? <p className="text-sm text-slate-500">Loading requests…</p> : null}

      {view === 'grouped' ? (
        <div className="space-y-4">
          {groups.map((group) => {
            const openIds = group.requests.filter((row) => row.status === 'open').map((row) => row.id);
            const checked = openIds.length > 0 && openIds.every((id) => selectedSet.has(id));
            return (
              <article key={group.key} className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <label className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" checked={checked} disabled={!openIds.length} onChange={() => toggleGroup(group)} />
                    <span>
                      <span className="text-lg font-semibold text-slate-950">{group.partName}</span>
                      <span className="ml-2 text-sm font-semibold text-slate-600">need {group.need}</span>
                      <span className="mt-1 block text-sm text-slate-500">
                        {group.sku ? `SKU ${group.sku} · ` : ''}
                        in stock {group.qtyOnHand} · on order {group.qtyOnOrder}
                      </span>
                      <span className="block text-sm text-slate-600">
                        {group.preferredSupplierName
                          ? `Preferred: ${group.preferredSupplierName}${group.preferredCost ? ` ${formatCurrency(group.preferredCost)}` : ''}${group.leadTimeDays != null ? ` · ${group.leadTimeDays} day lead` : ''}`
                          : 'No preferred supplier'}
                      </span>
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-xl border px-3 py-2 text-sm font-semibold"
                      disabled={!group.inventoryItemId || group.qtyOnHand <= 0 || openIds.length === 0 || allocateMutation.isPending}
                      onClick={() => allocateMutation.mutate(openIds)}
                    >
                      Allocate stock
                    </button>
                  </div>
                </div>
                <ul className="mt-3 divide-y text-sm">
                  {group.requests.map((row) => (
                    <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                      <span>
                        <Link className="font-semibold text-primary" to={`/tickets/${row.ticketId}`}>{row.ticketNumber}</Link>
                        <span className="ml-2">{row.customerName}</span>
                        <span className="ml-2 text-slate-500">requested {ageLabel(row.ageHours)}</span>
                        {row.poNumber ? <span className="ml-2 text-slate-500">{row.poNumber}</span> : null}
                      </span>
                      {row.status === 'open' || row.status === 'ordered' || row.status === 'in_transit' ? (
                        <button type="button" className="text-xs font-semibold text-red-700" onClick={() => setCancelId(row.id)}>
                          Cancel
                        </button>
                      ) : (
                        <span className="text-xs text-slate-500">{prettyLabel(row.status)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
          {!queueQuery.isLoading && groups.length === 0 ? (
            <p className="rounded-2xl border bg-white px-4 py-8 text-center text-sm text-slate-500">No parts requests match these filters.</p>
          ) : null}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Ticket</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Part</th>
                <th className="px-3 py-2">Requested by</th>
                <th className="px-3 py-2">Age</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Promised</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {flat.map((row) => (
                <tr key={row.id}>
                  <td className="px-3 py-2"><Link className="font-semibold text-primary" to={`/tickets/${row.ticketId}`}>{row.ticketNumber}</Link></td>
                  <td className="px-3 py-2">{row.customerName}</td>
                  <td className="px-3 py-2">{row.partName}</td>
                  <td className="px-3 py-2">{row.requestedBy ?? '—'}</td>
                  <td className="px-3 py-2">{ageLabel(row.ageHours)}</td>
                  <td className="px-3 py-2">{prettyLabel(row.status)}{row.poNumber ? ` · ${row.poNumber}` : ''}</td>
                  <td className="px-3 py-2">{row.promisedDate ? row.promisedDate.slice(0, 10) : '—'}</td>
                  <td className="px-3 py-2">
                    {row.status === 'open' || row.status === 'ordered' || row.status === 'in_transit' ? (
                      <button type="button" className="text-xs font-semibold text-red-700" onClick={() => setCancelId(row.id)}>Cancel</button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 p-4">
          <form
            className="mt-10 w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-2xl"
            onSubmit={(event) => {
              event.preventDefault();
              poMutation.mutate();
            }}
          >
            <h2 className="text-lg font-semibold">Create purchase orders</h2>
            {supplierCount > 1 ? (
              <p className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                These parts come from {supplierCount} suppliers. This will create {supplierCount} draft purchase orders.
              </p>
            ) : (
              <p className="text-sm text-slate-600">One draft purchase order will be created. Quantities can be raised above the request count.</p>
            )}
            <div className="space-y-2">
              {selectedGroups.map((group) => (
                <label key={group.key} className="flex items-center justify-between gap-3 text-sm">
                  <span>{group.partName}{group.preferredSupplierName ? ` · ${group.preferredSupplierName}` : ''}</span>
                  <input
                    className="w-20 rounded-xl border px-2 py-1"
                    type="number"
                    min="1"
                    value={qtyDraft[group.key] ?? String(group.need)}
                    onChange={(event) => setQtyDraft((current) => ({ ...current, [group.key]: event.target.value }))}
                  />
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold" onClick={() => setConfirmOpen(false)}>Back</button>
              <button type="submit" className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white" disabled={poMutation.isPending}>
                {poMutation.isPending ? 'Creating…' : 'Create drafts'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {cancelId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <form
            className="w-full max-w-md space-y-3 rounded-2xl bg-white p-6 shadow-2xl"
            onSubmit={(event) => {
              event.preventDefault();
              if (!cancelReason.trim()) return;
              cancelMutation.mutate();
            }}
          >
            <h2 className="text-lg font-semibold">Cancel request</h2>
            <textarea
              required
              className="min-h-24 w-full rounded-xl border px-3 py-2 text-sm"
              placeholder="Why is this request being cancelled?"
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold" onClick={() => setCancelId(null)}>Back</button>
              <button type="submit" className="rounded-xl bg-red-700 px-4 py-2 text-sm font-semibold text-white" disabled={cancelMutation.isPending}>
                Cancel request
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
